import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { javascript } from "@codemirror/lang-javascript";
import { oneDark } from "@codemirror/theme-one-dark";
import { EditorView } from "@codemirror/view";
import Profile from "../Profile";
import Header from "../components/Header";
import UserMenu from "../components/UserMenu";
import Button from "../components/Button";
import Card from "../components/Card";
import Modal from "../components/Modal";
import { getUser, removeUser, getInitials, saveUser } from "../utils/auth";
import { getSavedPen, savePenToStorage, clearPenStorage, getDefaultPen, MAX_FREE_PENS } from "../utils/penStorage";
import "../App.css";

const API = '/api';

// Parses the AI endpoint's ###MARKER### delimited stream. Called on every chunk with the
// full accumulated text so far — the last section in the text is still growing, which is
// what produces the live-typing effect as its content is re-derived on each call.
function parseAiStream(text) {
  const markerRe = /###(NAME|HTML|CSS|JS|END)###/g;
  const hits = [];
  let m;
  while ((m = markerRe.exec(text)) !== null) {
    hits.push({ key: m[1], markerStart: m.index, contentStart: m.index + m[0].length });
  }
  const sections = {};
  hits.forEach((hit, i) => {
    if (hit.key === 'END') return;
    const contentEnd = i + 1 < hits.length ? hits[i + 1].markerStart : text.length;
    sections[hit.key] = text.slice(hit.contentStart, contentEnd).replace(/^\n/, '').replace(/\n$/, '');
  });
  const complete = hits.some(h => h.key === 'END');
  return { sections, complete };
}

function LazyIframe({ srcDoc, title }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} style={{width:'100%', height:'100%'}}>
      {visible ? (
        <iframe title={title} sandbox="allow-scripts" srcDoc={srcDoc}
          style={{width:'100%', height:'100%', border:'none', pointerEvents:'none'}} />
      ) : (
        <div style={{width:'100%', height:'100%', background:'#f0f0f0', display:'flex', alignItems:'center', justifyContent:'center'}}>
          <span style={{color:'#999', fontSize:'12px'}}>Loading...</span>
        </div>
      )}
    </div>
  );
}

function PenSkeleton() {
  return (
    <div className="pen-card" style={{cursor:'default'}}>
      <div style={{width:'100%', height:'140px', background:'linear-gradient(90deg, #2d2d2d 25%, #3a3a3a 50%, #2d2d2d 75%)', backgroundSize:'200% 100%', animation:'shimmer 1.5s infinite'}} />
      <div className="pen-card-footer">
        <div style={{width:'24px', height:'24px', borderRadius:'50%', background:'#3a3a3a'}} />
        <div style={{flex:1, display:'flex', flexDirection:'column', gap:'4px'}}>
          <div style={{width:'70%', height:'10px', background:'#3a3a3a', borderRadius:'4px'}} />
          <div style={{width:'40%', height:'8px', background:'#3a3a3a', borderRadius:'4px'}} />
        </div>
      </div>
    </div>
  );
}

export default function EditorPage() {
  const navigate                        = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [user, setUser] = useState(getUser);

  const savedPen = getSavedPen();
  const [htmlCode,     setHtmlCode]     = useState(savedPen?.html  ?? getDefaultPen().html);
  const [cssCode,      setCssCode]      = useState(savedPen?.css   ?? getDefaultPen().css);
  const [jsCode,       setJsCode]       = useState(savedPen?.js    ?? getDefaultPen().js);
  const [penTitle,     setPenTitle]     = useState(savedPen?.title ?? getDefaultPen().title);
  const [currentPenId, setCurrentPenId] = useState(savedPen?.id   ?? null);

  const [activeTab,    setActiveTab]    = useState('html');
  const [savedPens,    setSavedPens]    = useState([]);
  const [pensLoading,  setPensLoading]  = useState(false);
  const [pensLoadingMore, setPensLoadingMore] = useState(false);
  const [pensPage,     setPensPage]     = useState(1);
  const [pensHasMore,  setPensHasMore]  = useState(false);
  const [showPens,     setShowPens]     = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [pensView,     setPensView]     = useState('grid');
  const [showShare,    setShowShare]    = useState(false);
  const [shareUrl,     setShareUrl]     = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [isEditing,    setIsEditing]    = useState(false);
  const [saveNotif,    setSaveNotif]    = useState('');
  const [showProfile,  setShowProfile]  = useState(false);
  const [editorH,      setEditorH]      = useState(220);
  const [col1W,        setCol1W]        = useState(33.33);
  const [col2W,        setCol2W]        = useState(33.33);
  const [col3W,        setCol3W]        = useState(33.34);
  const [cursorLine,   setCursorLine]   = useState(1);
  const [cursorCol,    setCursorCol]    = useState(1);
  const [activeEditor, setActiveEditor] = useState('HTML');
  const [showAI,        setShowAI]        = useState(false);
  const [aiPrompt,      setAiPrompt]      = useState('');
  const [aiLoading,     setAiLoading]     = useState(false);
  const [aiThinking,    setAiThinking]    = useState(false);
  const [aiError,       setAiError]       = useState('');
  const [aiModel,       setAiModel]       = useState('gemini');
  const [aiModelStatus, setAiModelStatus] = useState({
    gemini: { label: 'Gemini', available: true, reason: null },
    groq:   { label: 'Groq',   available: true, reason: null },
  });

  useEffect(() => {
    if (searchParams.get('settings') === 'true') { setShowProfile(true); setSearchParams({}); }
    if (searchParams.get('pens')     === 'true') { setShowPens(true);    setSearchParams({}); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const params   = new URLSearchParams(window.location.search);
    const userData = params.get('user');
    const penToken = params.get('pen');

    if (penToken) {
      fetch(`${API}/get_shared_pen.php?token=${penToken}`)
        .then(r => r.json())
        .then(data => {
          if (!data.error) {
            sessionStorage.setItem('wsncode_shared_pen', JSON.stringify(data));
            navigate('/shared');
          }
        });
      window.history.replaceState({}, '', '/editor');
    }

    if (userData) {
      try {
        const parsedUser = JSON.parse(decodeURIComponent(userData));
        saveUser(parsedUser);
        setUser(parsedUser);
        window.history.replaceState({}, '', '/editor');
      } catch (e) { console.error('Login parse error'); }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user) loadAllPens();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ctrl+S shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        savePen();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [htmlCode, cssCode, jsCode, penTitle]); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => { removeUser(); setUser(null); navigate('/'); };

  const newPen = () => {
    const def = getDefaultPen();
    setCurrentPenId(null);
    setPenTitle(def.title);
    setHtmlCode(def.html);
    setCssCode(def.css);
    setJsCode(def.js);
    clearPenStorage();
  };

  const cursorExtension = (label) => EditorView.updateListener.of((update) => {
    if (update.selectionSet) {
      setActiveEditor(label);
      const pos  = update.state.selection.main.head;
      const line = update.state.doc.lineAt(pos);
      setCursorLine(line.number);
      setCursorCol(pos - line.from + 1);
    }
  });

  const startVerticalResize = (e) => {
    e.preventDefault(); e.stopPropagation();
    const startY = e.touches ? e.touches[0].clientY : e.clientY;
    const startH = editorH;
    const move = (ev) => {
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      setEditorH(Math.min(Math.max(startH + (clientY - startY), 80), 700));
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('touchend', up);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', up);
  };

  const startH1Resize = (e) => {
    e.preventDefault();
    const x0 = e.clientX; const c1 = col1W; const c2 = col2W;
    const move = (ev) => {
      const d = (ev.clientX - x0) / window.innerWidth * 100;
      setCol1W(Math.min(Math.max(c1 + d, 10), 80));
      setCol2W(Math.min(Math.max(c2 - d, 10), 80));
    };
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.body.style.cursor = 'ew-resize'; document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  };

  const startH2Resize = (e) => {
    e.preventDefault();
    const x0 = e.clientX; const c2 = col2W; const c3 = col3W;
    const move = (ev) => {
      const d = (ev.clientX - x0) / window.innerWidth * 100;
      setCol2W(Math.min(Math.max(c2 + d, 10), 80));
      setCol3W(Math.min(Math.max(c3 - d, 10), 80));
    };
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.body.style.cursor = 'ew-resize'; document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  };

  const getPreview = useCallback(() => {
    return `<!DOCTYPE html><html><head><style>${cssCode}</style></head><body>${htmlCode}<script>${jsCode}</script></body></html>`;
  }, [htmlCode, cssCode, jsCode]);

  // ── API calls ─────────────────────────────────────────────────────────────

  // ✅ FIX 2: id bhi bhejo — duplicate nahi banega
  const savePen = async () => {
    if (!user) { navigate('/login'); return; }
    if (!currentPenId && savedPens.length >= MAX_FREE_PENS) {
      setSaveNotif(`✗ Free plan limit reached (${MAX_FREE_PENS} pens). Delete one or upgrade to Pro.`);
      setTimeout(() => setSaveNotif(''), 4000);
      return;
    }
    try {
      const response = await fetch(`${API}/save_pen.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title:   penTitle,
          html:    htmlCode,
          css:     cssCode,
          js:      jsCode,
          user_id: user.id,
          id:      currentPenId ?? 0  // ✅ ID bhejo
        })
      });
      const data = await response.json();
      if (data.success) {
        setCurrentPenId(data.id);
        savePenToStorage({ id: data.id, html: htmlCode, css: cssCode, js: jsCode, title: penTitle });
        setSaveNotif(data.updated ? '✓ Updated!' : '✓ Saved!');
        setTimeout(() => setSaveNotif(''), 2000);
        loadAllPens();
      } else {
        setSaveNotif('✗ Error!');
        setTimeout(() => setSaveNotif(''), 2000);
      }
    } catch {
      setSaveNotif('✗ Error!');
      setTimeout(() => setSaveNotif(''), 2000);
    }
  };

  const sharePen = async () => {
    if (!user)         { navigate('/login'); return; }
    if (!currentPenId) { alert('Pehle pen save karo!'); return; }
    setShareLoading(true);
    try {
      const response = await fetch(`${API}/share_pen.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pen_id: currentPenId, user_id: user.id })
      });
      const data = await response.json();
      if (data.success) { setShareUrl(data.url); setShowShare(true); }
      else alert('Error: ' + data.message);
    } catch { alert('Error sharing pen!'); }
    setShareLoading(false);
  };

  const deletePen = async (pen_id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this pen?')) return;
    try {
      const response = await fetch(`${API}/delete_pen.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pen_id, user_id: user.id })
      });
      const data = await response.json();
      if (data.success) {
        const updated = savedPens.filter(p => p.id !== pen_id);
        setSavedPens(updated);
        localStorage.setItem('wsncode_pens_cache', JSON.stringify(updated));
      }
    } catch { console.error('Error deleting pen'); }
  };

  const fetchAiModelStatus = async () => {
    try {
      const response = await fetch(`${API}/ai_model_status.php`);
      const data = await response.json();
      if (data.success) setAiModelStatus(data.models);
    } catch { /* status check is best-effort; dropdown just stays as-is */ }
  };

  const generateWithAI = async () => {
    if (!user) { navigate('/login'); return; }
    if (!aiPrompt.trim()) return;
    if (aiModelStatus[aiModel] && !aiModelStatus[aiModel].available) return;
    setAiLoading(true);
    setAiThinking(true);
    setAiError('');
    try {
      const response = await fetch(`${API}/ai_generate.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          model:   aiModel,
          prompt:  aiPrompt,
          html:    htmlCode,
          css:     cssCode,
          js:      jsCode,
        })
      });

      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || contentType.includes('application/json')) {
        const data = await response.json();
        setAiError(data.message || 'Something went wrong. Please try again.');
        if (data.model_status) {
          setAiModelStatus(prev => ({ ...prev, [aiModel]: { ...prev[aiModel], ...data.model_status } }));
        }
        setAiLoading(false);
        setAiThinking(false);
        return;
      }

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText     = '';
      let sawFirstByte = false;

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        fullText += decoder.decode(value, { stream: true });
        if (!sawFirstByte) { sawFirstByte = true; setAiThinking(false); }
        const { sections } = parseAiStream(fullText);
        if (sections.HTML !== undefined) setHtmlCode(sections.HTML);
        if (sections.CSS  !== undefined) setCssCode(sections.CSS);
        if (sections.JS   !== undefined) setJsCode(sections.JS);
      }

      const { sections, complete } = parseAiStream(fullText);
      const ok = complete && sections.NAME !== undefined && sections.HTML !== undefined
                 && sections.CSS !== undefined && sections.JS !== undefined;

      if (!ok) {
        if (fullText.includes('###TRUNCATED###')) {
          setAiError('This request generated too much content and was cut off. Try a simpler request, or break it into smaller steps.');
        } else if (fullText.includes('###BLOCKED###')) {
          setAiError("This request was blocked by the AI service's content filters. Please try rephrasing it.");
        } else {
          setAiError("The AI's response couldn't be understood. Please try rephrasing your request.");
        }
      } else {
        setHtmlCode(sections.HTML);
        setCssCode(sections.CSS);
        setJsCode(sections.JS);
        const aiName = sections.NAME.trim();
        if (aiName && penTitle === 'Untitled') {
          setPenTitle(aiName);
        }
      }
    } catch {
      setAiError('Could not reach the server. Please try again.');
    }
    setAiLoading(false);
    setAiThinking(false);
  };

  const loadAllPens = async () => {
    if (!user) return;
    const cached = localStorage.getItem('wsncode_pens_cache');
    if (cached) {
      try { setSavedPens(JSON.parse(cached)); } catch {}
    } else {
      setPensLoading(true);
    }
    setPensPage(1);
    try {
      const controller = new AbortController();
      const timeout    = setTimeout(() => controller.abort(), 8000);
      const response   = await fetch(`${API}/get_user_pens.php?user_id=${user.id}&page=1`, { signal: controller.signal });
      clearTimeout(timeout);
      const data = await response.json();
      const pens = Array.isArray(data.pens) ? data.pens : [];
      setSavedPens(pens);
      setPensHasMore(!!data.has_more);
      localStorage.setItem('wsncode_pens_cache', JSON.stringify(pens));
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error loading pens');
    }
    setPensLoading(false);
  };

  const loadMorePens = async () => {
    if (!user) return;
    const nextPage = pensPage + 1;
    setPensLoadingMore(true);
    try {
      const response = await fetch(`${API}/get_user_pens.php?user_id=${user.id}&page=${nextPage}`);
      const data = await response.json();
      const more = Array.isArray(data.pens) ? data.pens : [];
      setSavedPens(prev => {
        const updated = [...prev, ...more];
        localStorage.setItem('wsncode_pens_cache', JSON.stringify(updated));
        return updated;
      });
      setPensHasMore(!!data.has_more);
      setPensPage(nextPage);
    } catch (err) {
      console.error('Error loading more pens');
    }
    setPensLoadingMore(false);
  };

  const loadPen = async (id) => {
    const cached = localStorage.getItem('wsncode_pens_cache');
    if (cached) {
      const pens = JSON.parse(cached);
      const pen  = pens.find(p => p.id === id);
      if (pen) {
        setHtmlCode(pen.html || '');
        setCssCode(pen.css   || '');
        setJsCode(pen.js     || '');
        setPenTitle(pen.title || 'Untitled');
        setCurrentPenId(id);
        savePenToStorage({ id, html: pen.html, css: pen.css, js: pen.js, title: pen.title });
        setShowPens(false);
        return;
      }
    }
    setLoading(true);
    try {
      const controller = new AbortController();
      const timeout    = setTimeout(() => controller.abort(), 8000);
      const response   = await fetch(`${API}/get_pen.php?id=${id}`, { signal: controller.signal });
      clearTimeout(timeout);
      const data = await response.json();
      if (!data.error) {
        setHtmlCode(data.html);
        setCssCode(data.css);
        setJsCode(data.js);
        setPenTitle(data.title);
        setCurrentPenId(id);
        savePenToStorage({ id, html: data.html, css: data.css, js: data.js, title: data.title });
        setShowPens(false);
      }
    } catch { alert('Error loading pen!'); }
    setLoading(false);
  };

  // ── PensPopup ─────────────────────────────────────────────────────────────
  const PensPopup = ({ onClose }) => (
    <div className="pens-overlay" onClick={(e) => { if (e.target.className === 'pens-overlay') onClose(); }}>
      <div className="pens-panel">
        <div className="pens-panel-header">
          <h3 className="pens-title">My Pens</h3>
          <div className="pens-view-toggle">
            <button className={`view-btn ${pensView === 'grid' ? 'active' : ''}`} onClick={() => setPensView('grid')}>⊞</button>
            <button className={`view-btn ${pensView === 'list' ? 'active' : ''}`} onClick={() => setPensView('list')}>☰</button>
          </div>
          <button className="pens-close-btn" onClick={onClose}>✕</button>
        </div>

        {pensLoading ? (
          <div className="pens-grid">
            {[1,2,3,4,5,6].map(i => <PenSkeleton key={i} />)}
          </div>
        ) : savedPens.length === 0 ? (
          <div className="no-pens-box">
            <p className="no-pens-title">You don't have any pens yet.</p>
            <p className="no-pens-sub">Start coding and save your work here!</p>
            <Button variant="primary" onClick={() => { newPen(); onClose(); }}>✏️ Create New Pen</Button>
          </div>
        ) : pensView === 'grid' ? (
          <div className="pens-grid">
            {savedPens.map((pen) => (
              <Card hoverable key={pen.id} className="pen-card">
                <div className="pen-card-preview" onClick={() => { loadPen(pen.id); onClose(); }}>
                  <LazyIframe
                    title={`preview-${pen.id}`}
                    srcDoc={`<!DOCTYPE html><html><head><style>${pen.css||''}</style></head><body>${pen.html||''}<script>${pen.js||''}</script></body></html>`}
                  />
                </div>
                <div className="pen-card-footer">
                  <div className="pen-card-avatar">{getInitials(user.name)}</div>
                  <div className="pen-card-info" onClick={() => { loadPen(pen.id); onClose(); }}>
                    <span className="pen-card-name">{pen.title || 'Untitled'}</span>
                    <span className="pen-card-user">{user.name}</span>
                  </div>
                  <Button variant="danger" size="sm" onClick={(e) => deletePen(pen.id, e)}>🗑️</Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="pens-list">
            {savedPens.map((pen) => (
              <div key={pen.id} className="pen-item">
                <div className="pen-item-avatar">{getInitials(user.name)}</div>
                <div className="pen-item-info" onClick={() => { loadPen(pen.id); onClose(); }}>
                  <span className="pen-name">{pen.title || 'Untitled'}</span>
                  <span className="pen-item-user">{user.name}</span>
                </div>
                <span className="pen-date">{new Date(pen.created_at).toLocaleDateString()}</span>
                <Button variant="danger" size="sm" onClick={(e) => deletePen(pen.id, e)}>🗑️</Button>
              </div>
            ))}
          </div>
        )}

        {!pensLoading && pensHasMore && (
          <div className="pens-load-more">
            <Button variant="secondary" onClick={loadMorePens} disabled={pensLoadingMore}>
              {pensLoadingMore ? 'Loading...' : 'Load More'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="app">

      <Header
        onLogoClick={() => navigate('/')}
        navItems={[
          { label: 'Home',    onClick: () => navigate('/') },
          { label: 'Explore', onClick: () => navigate('/explore') },
        ]}
        leftExtra={
          <div className="header-title-group">
            <div className="header-pen-title">
              {isEditing ? (
                <input className="header-pen-input" value={penTitle} autoFocus
                  onChange={(e) => setPenTitle(e.target.value)}
                  onBlur={() => setIsEditing(false)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setIsEditing(false); }} />
              ) : (
                <span className="header-pen-name" onClick={() => setIsEditing(true)} title="Click to rename">{penTitle}</span>
              )}
              <button className="header-edit-title" onClick={() => setIsEditing(true)} title="Rename">✏️</button>
            </div>
            {user && <span className="header-username">👤 {user.name}</span>}
          </div>
        }
        rightExtra={
          user && (
            <>
              <Button variant="primary" size="sm" onClick={savePen} title="Save (Ctrl+S)">💾 Save</Button>
              {saveNotif && <span className="save-notif">{saveNotif}</span>}
              <button className="header-icon-btn" title="AI Generate" onClick={() => { if (!showAI) fetchAiModelStatus(); setShowAI(!showAI); }}>✨</button>
              <button className="header-icon-btn" title="Share Pen" onClick={sharePen}>{shareLoading ? '⏳' : '🔗'}</button>
              <button className="header-icon-btn" title="My Pens"   onClick={() => setShowPens(!showPens)}>🗂️</button>
              <button className="header-icon-btn" title="New Pen"   onClick={newPen}>✚</button>
            </>
          )
        }
        authSlot={
          user ? (
            <UserMenu
              user={user}
              onLogout={logout}
              items={[
                { label: 'New Pen',   icon: '✏️', onClick: newPen },
                { label: 'Home',      icon: '🏠', onClick: () => navigate('/') },
                { label: 'My Pens',   icon: '📁', onClick: () => setShowPens(true) },
                { label: 'Explore',   icon: '🔥', onClick: () => navigate('/explore') },
                { label: 'Settings',  icon: '⚙️', onClick: () => setShowProfile(true) },
              ]}
            />
          ) : (
            <Button variant="secondary" size="sm" onClick={() => navigate('/login')}>🔑 Login</Button>
          )
        }
      />

      {showPens && user && <PensPopup onClose={() => setShowPens(false)} />}
      {loading  && <div className="loading">Loading pen...</div>}

      {showAI && user && (
        <div className="ai-overlay" onClick={(e) => { if (e.target.className === 'ai-overlay') setShowAI(false); }}>
          <div className="ai-panel">
            <div className="ai-panel-header">
              <span className="ai-panel-title">✨ AI Generate</span>
              <button className="ai-panel-close" onClick={() => setShowAI(false)}>✕</button>
            </div>
            <p className="ai-panel-desc">
              Describe what you want and AI will generate or modify your HTML, CSS and JS.
            </p>
            <select
              className="ai-model-select"
              value={aiModel}
              onChange={(e) => setAiModel(e.target.value)}
              disabled={aiLoading}
            >
              {Object.entries(aiModelStatus).map(([key, m]) => (
                <option key={key} value={key} disabled={!m.available}>
                  {m.label}{!m.available ? ' (unavailable)' : ''}
                </option>
              ))}
            </select>
            {aiModelStatus[aiModel] && !aiModelStatus[aiModel].available && (
              <p className="ai-panel-error">
                {aiModelStatus[aiModel].label} is unavailable right now
                {aiModelStatus[aiModel].reason ? ` (${aiModelStatus[aiModel].reason})` : ''}. Try a different model.
              </p>
            )}
            <textarea
              className="ai-textarea"
              placeholder="e.g. a red button, a simple todo list app..."
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              disabled={aiLoading}
            />
            {aiLoading && (
              <p className="ai-panel-status">
                {aiThinking ? '🤔 Thinking...' : '⌨️ Generating...'}
              </p>
            )}
            {aiError && <p className="ai-panel-error">{aiError}</p>}
            <div className="ai-panel-actions">
              <Button
                variant="primary"
                onClick={generateWithAI}
                disabled={aiLoading || !aiPrompt.trim() || (aiModelStatus[aiModel] && !aiModelStatus[aiModel].available)}
              >
                {aiLoading ? (aiThinking ? '🤔 Thinking...' : '⌨️ Generating...') : '✨ Generate'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="editor-tabs">
        <div className={`editor-tab ${activeTab === 'html' ? 'active' : ''}`} onClick={() => setActiveTab('html')}>HTML</div>
        <div className={`editor-tab ${activeTab === 'css'  ? 'active' : ''}`} onClick={() => setActiveTab('css')}>CSS</div>
        <div className={`editor-tab ${activeTab === 'js'   ? 'active' : ''}`} onClick={() => setActiveTab('js')}>JS</div>
      </div>

      <div className="editors-wrapper">
        <div className="editors-container" style={{height:`${editorH}px`}}>

          <div className={`editor-panel ${activeTab === 'html' ? 'active' : ''}`} style={{width:`${col1W}%`}}>
            <div className="editor-label">HTML</div>
            <CodeMirror value={htmlCode} height={`${editorH - 35}px`} theme={oneDark}
              extensions={[html(), EditorView.lineWrapping, cursorExtension('HTML')]}
              basicSetup={{ lineNumbers: true, foldGutter: false }}
              onChange={(val) => setHtmlCode(val)} />
          </div>

          <div className="resize-handle-h" onMouseDown={startH1Resize} />

          <div className={`editor-panel ${activeTab === 'css' ? 'active' : ''}`} style={{width:`${col2W}%`}}>
            <div className="editor-label">CSS</div>
            <CodeMirror value={cssCode} height={`${editorH - 35}px`} theme={oneDark}
              extensions={[css(), EditorView.lineWrapping, cursorExtension('CSS')]}
              basicSetup={{ lineNumbers: true, foldGutter: false }}
              onChange={(val) => setCssCode(val)} />
          </div>

          <div className="resize-handle-h" onMouseDown={startH2Resize} />

          <div className={`editor-panel ${activeTab === 'js' ? 'active' : ''}`} style={{width:`${col3W}%`}}>
            <div className="editor-label">JS</div>
            <CodeMirror value={jsCode} height={`${editorH - 35}px`} theme={oneDark}
              extensions={[javascript(), EditorView.lineWrapping, cursorExtension('JS')]}
              basicSetup={{ lineNumbers: true, foldGutter: false }}
              onChange={(val) => setJsCode(val)} />
          </div>

        </div>
        <div className="resize-handle-v" onMouseDown={startVerticalResize} onTouchStart={startVerticalResize}>
          <span className="resize-dots">• • •</span>
        </div>
      </div>

      <div className="preview-area">
        <div className="editor-label">Preview</div>
        <iframe title="preview" sandbox="allow-scripts" srcDoc={getPreview()}
          style={{width:"100%", height:"100%", border:"none", background:"#fff"}} />
      </div>

      <div className="status-bar status-bar--visible">
        <div className="status-bar-left">
          <span><span className="status-dot" /> WSNCode</span>
          <span>⎇ main</span>
          {saveNotif && <span style={{background:'#ffffff22', padding:'1px 8px', borderRadius:'10px'}}>{saveNotif}</span>}
        </div>
        <div className="status-bar-center">{penTitle}</div>
        <div className="status-bar-right">
          <span>{activeEditor}</span>
          <span>Ln {cursorLine}, Col {cursorCol}</span>
          <span>UTF-8</span>
        </div>
      </div>

      {showShare && (
        <Modal title="🔗 Share Pen" maxWidth={480} onClose={() => setShowShare(false)}>
          <p className="share-desc">Anyone with this link can view your pen:</p>
          <div className="share-url-box">
            <input className="share-url-input" value={shareUrl} readOnly onClick={(e) => e.target.select()} />
            <Button variant="primary" onClick={() => { navigator.clipboard.writeText(shareUrl); alert('Link copied!'); }}>Copy</Button>
          </div>
          <div className="share-actions">
            <a href={shareUrl} target="_blank" rel="noreferrer" className="share-open-btn">Open in new tab →</a>
          </div>
        </Modal>
      )}

      {showProfile && user && (
        <Profile user={user} onUpdate={(updatedUser) => { saveUser(updatedUser); setUser(updatedUser); }} onClose={() => setShowProfile(false)} />
      )}
    </div>
  );
}